/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import RecentSearches from './recent-searches';
import SearchSuggestionCategories from './search-suggestion-categories';
import SuggestionSection from './suggestions-section';
import type { SearchSuggestions } from './types';
import {
    isDefaultViewportEligible,
    type ManualCategoriesStatus,
    type OpenSearchViewport,
} from './default-search-viewport';
import { cn } from '@/lib/utils';

interface SuggestionsProps {
    searchSuggestions: SearchSuggestions | null;
    recentSearches: string[];
    closeAndNavigate: (link: string) => void;
    clearRecentSearches: () => void;
    /** Which open panel mode to render — driven by `resolveSearchViewport`. */
    viewport: OpenSearchViewport;
    categoriesStatus?: ManualCategoriesStatus;
    categoriesMarkup?: string;
}

export default function Suggestions({
    searchSuggestions,
    recentSearches,
    closeAndNavigate,
    clearRecentSearches,
    viewport,
    categoriesStatus = 'absent',
    categoriesMarkup,
}: SuggestionsProps) {
    const isDefaultViewport = viewport === 'default';
    const hasCategories = Boolean(searchSuggestions?.categorySuggestions?.length);
    const hasCategoryPhraseSuggestions = Boolean(searchSuggestions?.categoryPhraseSuggestions?.length);
    const hasProducts = Boolean(searchSuggestions?.productSuggestions?.length);
    const hasPopularSearches = Boolean(searchSuggestions?.popularSearchSuggestions?.length);
    const hasApiSuggestions = hasCategories || hasCategoryPhraseSuggestions || hasProducts || hasPopularSearches;
    const showApiSuggestions = viewport === 'typed' && Boolean(searchSuggestions) && hasApiSuggestions;
    const showRecentSearches = isDefaultViewport && recentSearches.length > 0;
    const showManualCategories = isDefaultViewport && isDefaultViewportEligible(0, categoriesStatus);

    if (!showApiSuggestions && !showRecentSearches && !showManualCategories) {
        return null;
    }

    return (
        <div
            className={cn(
                'section-container flex flex-col gap-4 p-4 lg:pt-4 lg:px-16 lg:pb-12',
                showApiSuggestions && 'pb-8'
            )}>
            {showApiSuggestions && searchSuggestions ? (
                <SuggestionSection searchSuggestions={searchSuggestions} closeAndNavigate={closeAndNavigate} />
            ) : (
                <>
                    {showRecentSearches && (
                        <RecentSearches
                            recentSearches={recentSearches}
                            closeAndNavigate={closeAndNavigate}
                            clearRecentSearches={clearRecentSearches}
                        />
                    )}
                    {showManualCategories && (
                        <SearchSuggestionCategories
                            status={categoriesStatus}
                            categories={categoriesMarkup}
                            closeAndNavigate={closeAndNavigate}
                        />
                    )}
                </>
            )}
        </div>
    );
}
